import { useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { NavLink } from 'react-router-dom'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useProcotol } from '@services/protocols/protocols.hook'
import { PROTOCOL_STATUS } from '@services/protocols/protocols.consts'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material'
import { ExpandMore, ArrowBackIos } from '@mui/icons-material'
import ProtocolGeneralInformation from './ProtocolGeneralInformation'
import ProtocolFurnituresLEGACY from './ProtocolFurnituresLEGACY'
import ProtocolItems from './ProtocolItems'
import ProtocolKeys from './ProtocolKeys'
import ProtocolMeters from './ProtocolMeters'
import ProtocolRooms from './ProtocolRooms'
import ProtocolSign from './ProtocolSign'
import ProtocolAnimals from './ProtocolAnimals'
import { useUserRoles } from '@services/token/token.hooks'
import ProtocolHouseholdAppliances from './ProtocolHouseholdAppliances'
import ProtocolFurnitures from './ProtocolFurnitures'

function Protocols() {
  const [expanded, setExpanded] = useState<string | false>(false)
  const { id } = useParams<{ id: string }>()
  const { protocol, isLoading } = useProcotol(Number(id))
  const { hash } = useLocation()
  const navigate = useNavigate()
  const { isTechnician } = useUserRoles()

  // Redirect technicians trying to access protocols
  useEffect(() => {
    if (isTechnician()) {
      navigate('/helpdesk/tickets', { replace: true })
    }
  }, [isTechnician, navigate])

  // If user is a technician, don't show the protocol
  if (isTechnician()) {
    return null
  }

  useEffect(() => {
    if (hash) {
      setExpanded(hash.substring(1))
    }
  }, [hash])

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!protocol) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <Typography color='white' variant='body2' fontWeight={700}>
          Nie odnaleziono protokołu
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box mb={2}>
        <IconButton component={NavLink} to='/'>
          <ArrowBackIos sx={{ color: 'white' }} />
        </IconButton>
      </Box>
      <Accordion expanded={expanded === 'info'} onChange={handleChange('info')}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls='info-content' id='info'>
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Ogólne informacje</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolGeneralInformation isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'furniture'} onChange={handleChange('furniture')}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls='furniture-content'
          id='furniture'
        >
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Meble (Legacy)</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolFurnituresLEGACY isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'items'} onChange={handleChange('items')}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls='items-content' id='items'>
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Przedmioty</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolItems isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'keys'} onChange={handleChange('keys')}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls='keys-content' id='keys'>
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Klucze</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolKeys isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'meters'} onChange={handleChange('meters')}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls='meters-content' id='meters'>
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Liczniki</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolMeters isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'rooms'} onChange={handleChange('rooms')}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls='rooms-content' id='rooms'>
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Pokoje</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolRooms isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'protocolFurnitures'}
        onChange={handleChange('protocolFurnitures')}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls='protocolFurnitures-content'
          id='protocolFurnitures'
        >
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Meble (Nowe)</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolFurnitures isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'protocolHouseholdAppliances'}
        onChange={handleChange('protocolHouseholdAppliances')}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls='householdAppliances-content'
          id='protocolHouseholdAppliances'
        >
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Sprzęt AGD</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolHouseholdAppliances isReadOnly={false} />
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'animals'} onChange={handleChange('animals')}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls='animals-content' id='animals'>
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Zwierzęta</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolAnimals isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'signature'} onChange={handleChange('signature')}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls='signature-content'
          id='signature'
        >
          <Typography sx={{ width: '33%', flexShrink: 0 }}>Podpis</Typography>
          <Typography fontSize='small' sx={{ color: 'text.secondary' }}></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProtocolSign isReadOnly={protocol.status !== PROTOCOL_STATUS.created} />
        </AccordionDetails>
      </Accordion>
      {/* {protocolFile?.file && (
        <a href={protocolFile?.file} target='_blank' rel='noopener noreferrer'>
          <Button variant='contained' sx={{ mt: 2 }}>
            Pobierz PDF
          </Button>
        </a>
      )} */}
    </Box>
  )
}

export default Protocols
